import { NativeBiometric, BiometryType } from 'capacitor-native-biometric'
import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const SETTINGS_KEY = 'app_lock_settings'
const LAST_ACTIVE_KEY = 'app_lock_last_active'

const API_URL = API_BASE_URL

interface AppLockSettings {
  biometricEnabled: boolean
  lockTimeout: number // minutes
}

const DEFAULT_SETTINGS: AppLockSettings = {
  biometricEnabled: false,
  lockTimeout: 2,
}

// Helper to get auth token
function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken
}

// Helper to make authenticated requests
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAccessToken()
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    credentials: 'include',
  })
}

export const appLockService = {
  // Check if running on native platform
  isNative(): boolean {
    return Capacitor.isNativePlatform()
  },

  // Get device-specific settings (biometric, timeout)
  async getSettings(): Promise<AppLockSettings> {
    try {
      const { value } = await Preferences.get({ key: SETTINGS_KEY })
      if (value) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(value) }
      }
    } catch {
      // Return defaults
    }
    return DEFAULT_SETTINGS
  },

  // Save device-specific settings
  async saveSettings(settings: Partial<AppLockSettings>): Promise<void> {
    const current = await this.getSettings()
    const updated = { ...current, ...settings }
    await Preferences.set({ key: SETTINGS_KEY, value: JSON.stringify(updated) })
  },

  // PIN Management - Server-side (synced across devices)
  async setPIN(pin: string): Promise<void> {
    const response = await authFetch(`${API_URL}/auth/pin`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    })

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Failed to set PIN')
    }
  },

  async verifyPIN(pin: string): Promise<boolean> {
    const response = await authFetch(`${API_URL}/auth/verify-pin`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    })

    if (!response.ok) {
      return false
    }

    const data = await response.json()
    return data.valid === true
  },

  async hasPIN(): Promise<boolean> {
    try {
      const response = await authFetch(`${API_URL}/auth/pin-status`)

      if (!response.ok) {
        return false
      }

      const data = await response.json()
      return data.hasPIN === true
    } catch {
      return false
    }
  },

  // Biometric
  async isBiometricAvailable(): Promise<{ available: boolean; type: BiometryType }> {
    if (!this.isNative()) {
      return { available: false, type: BiometryType.NONE }
    }
    try {
      const result = await NativeBiometric.isAvailable()
      return {
        available: result.isAvailable,
        type: result.biometryType,
      }
    } catch {
      return { available: false, type: BiometryType.NONE }
    }
  },

  async authenticateWithBiometric(reason?: string): Promise<boolean> {
    if (!this.isNative()) {
      return false
    }
    try {
      await NativeBiometric.verifyIdentity({
        reason: reason || 'Unlock Brightseed',
        title: 'Authenticate',
        subtitle: 'Use biometrics to unlock',
        description: 'Place your finger on the sensor or look at the camera',
      })
      return true
    } catch {
      return false
    }
  },

  getBiometryTypeName(type: BiometryType): string {
    switch (type) {
      case BiometryType.FACE_ID:
        return 'Face ID'
      case BiometryType.TOUCH_ID:
        return 'Touch ID'
      case BiometryType.FINGERPRINT:
        return 'Fingerprint'
      case BiometryType.FACE_AUTHENTICATION:
        return 'Face Authentication'
      case BiometryType.IRIS_AUTHENTICATION:
        return 'Iris Authentication'
      default:
        return 'Biometrics'
    }
  },

  // Lock timeout management (device-specific)
  async setLastActiveTime(): Promise<void> {
    await Preferences.set({
      key: LAST_ACTIVE_KEY,
      value: Date.now().toString(),
    })
  },

  async getLastActiveTime(): Promise<number> {
    const { value } = await Preferences.get({ key: LAST_ACTIVE_KEY })
    return value ? parseInt(value, 10) : Date.now()
  },

  async shouldLock(): Promise<boolean> {
    const hasPIN = await this.hasPIN()
    if (!hasPIN) return false

    const settings = await this.getSettings()
    const lastActive = await this.getLastActiveTime()
    const now = Date.now()
    const timeoutMs = settings.lockTimeout * 60 * 1000

    return now - lastActive > timeoutMs
  },

  // Clear device-specific data (on logout)
  async clearAll(): Promise<void> {
    await Preferences.remove({ key: SETTINGS_KEY })
    await Preferences.remove({ key: LAST_ACTIVE_KEY })
    // Note: PIN is stored on server, cleared when account is deleted
  },
}

export { BiometryType }
